import createClient from 'openapi-fetch'
import type { components, paths } from './gen/documents'
import { authFetch, call } from './http'

export type DocumentsSchemas = components['schemas']
export type Doc = DocumentsSchemas['Document']
export type DocKind = DocumentsSchemas['DocumentKind']
export type DocStatus = DocumentsSchemas['DocumentStatus']
export type DocFilter = { kind?: DocKind | ''; status?: DocStatus | '' }

const origin = typeof location !== 'undefined' ? location.origin : 'http://localhost'

export const documents = createClient<paths>({ baseUrl: `${origin}/api/documents`, fetch: authFetch })

export const listDocuments = (f: DocFilter = {}, cursor?: string) =>
  call(
    documents.GET('/documents', {
      params: {
        query: { ...(f.kind ? { kind: f.kind } : {}), ...(f.status ? { status: f.status } : {}), ...(cursor ? { cursor } : {}), limit: 50 },
      },
    }),
  )

/** Multipart upload. `created` is false when the same file (sha256) was already stored (200 instead of 201). */
export async function uploadDocument(file: File): Promise<{ doc: Doc; created: boolean }> {
  const fd = new FormData()
  fd.append('file', file)
  let created = false
  const doc = await call(
    documents.POST('/documents', { body: { file: '' }, bodySerializer: () => fd }).then((r) => {
      created = r.response.status === 201
      return r
    }),
  )
  return { doc, created }
}

/** The stored file as a Blob, fetched with the bearer header (never a token in the URL). */
export const downloadDocumentFile = (id: string): Promise<Blob> =>
  call(documents.GET('/documents/{id}/file', { params: { path: { id } }, parseAs: 'blob' })) as Promise<Blob>

/** Open the stored file in a new tab through a short-lived blob: URL. */
export async function openDocumentFile(id: string): Promise<void> {
  const tab = window.open('', '_blank') // opened inside the click handler so popup blockers allow it
  try {
    const url = URL.createObjectURL(await downloadDocumentFile(id))
    if (tab) tab.location.href = url
    else window.location.assign(url)
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  } catch (e) {
    tab?.close()
    throw e
  }
}
